import React, { useCallback, useContext, useMemo } from 'react';
import type { IntlShape } from 'react-intl';
import { IntlContext, injectIntl } from 'react-intl';
import type { FormatXMLElementFn } from 'intl-messageformat';

import type { TranslateFunction } from './i18n';
import { t } from './i18n';

export const useIntl = (): TranslateFunction => {
  const intl = useContext(IntlContext);
  return useMemo(() => t(intl), [intl]);
};

interface IntlProps {
  intl: IntlShape;
}

export interface TranslateProps extends IntlProps {
  t: TranslateFunction;
}

export const strong: FormatXMLElementFn<React.ReactNode, React.ReactNode> = chunks => <strong>{chunks}</strong>;

export function withIntl<TProps extends TranslateProps>(Component: React.ComponentType<TProps>) {
  // Explicit type parameters needed because the defaults are weird
  return injectIntl<'intl', Omit<TProps, 't'>>(({ intl, ...props }) => {
    // The cast is necessary because of https://github.com/Microsoft/TypeScript/issues/28938#issuecomment-450636046.
    // The inferred type is props: Omit<React.PropsWithChildren<TProps & IntlProps>, "intl">,
    // which should extend TProps.

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const translateFunction = useCallback(t(intl), [intl]);

    return <Component {...(props as TProps)} intl={intl} t={translateFunction} />;
  });
}

type SupportedLocales = 'en-GB' | 'es' | 'fr-CA' | 'sv' | 'en'
type OpenGraphLocale = 'en_US' | 'en_GB' | 'es_ES' | 'fr_CA' | 'sv_SE'

export const toOpenGraphLocale = (locale?: string | null): OpenGraphLocale => {
  switch (locale) {
    case 'en_GB':
    case 'en_GB_admin_locale':
    case 'en_GB_consumer_locale':
    case 'en-GB':
      return 'en_GB';
    case 'es':
    case 'es_admin_locale':
    case 'es_consumer_locale':
      return 'es_ES';
    case 'fr_CA':
    case 'fr_CA_admin_locale':
    case 'fr_CA_consumer_locale':
    case 'fr-CA':
      return 'fr_CA';
    case 'sv':
    case 'sv_admin_locale':
    case 'sv_consumer_locale':
      return 'sv_SE';
    case 'en':
    case 'en_admin_locale':
    case 'en_consumer_locale':
    default:
      return 'en_US';
  }
};

export const toReactIntlLocale = (locale?: string | null): SupportedLocales => {
  switch (locale) {
    case 'en_GB':
    case 'en_GB_admin_locale':
    case 'en_GB_consumer_locale':
      return 'en-GB';
    case 'es':
    case 'es_admin_locale':
    case 'es_consumer_locale':
      return 'es';
    case 'fr_CA':
    case 'fr_CA_admin_locale':
    case 'fr_CA_consumer_locale':
      return 'fr-CA';
    case 'sv':
    case 'sv_admin_locale':
    case 'sv_consumer_locale':
      return 'sv';
    case 'en':
    case 'en_admin_locale':
    case 'en_consumer_locale':
    default:
      return 'en';
  }
};
